import React, { useState, useEffect } from 'react';
import { Input, message, Modal } from '@brokerbay/design-components';
import isEmail from 'validator/es/lib/isEmail';
import { updateStripeRecipient } from 'api/BillingController';

type ManageRecipientModalProps = {
  onClose?: () => void;
  visible?: boolean;
  recipient?: string | null;
  setEmail?: React.Dispatch<React.SetStateAction<string | null>>;
  userId?: string;
  orgId?: string;
};

const ManageRecipientModal: React.FC<ManageRecipientModalProps> = ({
  onClose,
  visible,
  recipient,
  setEmail,
  userId,
  orgId,
}) => {
  const [localRecipient, setLocalRecipient] = useState('');
  const [loading, setLoading] = useState(false);

  const [showError, setError] = useState(false);

  const handleSave = async (email: string) => {
    try {
      setLoading(true);
      const {
        updatedEmail,
        successMessage,
      }: {
        updatedEmail: string;
        successMessage: string;
      } = await updateStripeRecipient(email, userId, orgId);
      if (setEmail) setEmail(updatedEmail);
      message.success(successMessage);
      onClose?.();
    } catch (error) {
      const errorMessage = error.message || 'An error occurred.';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setLocalRecipient(value);
    setError(false);
  };

  const handleConfirm = () => {
    const isValid = isEmail(localRecipient);

    if (!isValid) {
      message.error('Please provide a valid email address.');
      setError(true);
      return;
    }

    if (localRecipient === recipient) {
      message.info('No changes to save.');
      onClose?.();
      return;
    }

    setError(false);
    handleSave(localRecipient);
  };

  useEffect(() => {
    setLocalRecipient(recipient || '');
    setError(false);
  }, [recipient, visible]);

  return (
    <Modal
      open={visible}
      title="Manage Recipient"
      onCancel={loading ? undefined : onClose}
      okText="Save"
      onOk={handleConfirm}
      okButtonProps={{
        disabled: loading,
        loading,
      }}
      cancelButtonProps={{
        disabled: loading,
      }}
    >
      <Input
        value={localRecipient}
        onChange={handleChange}
        error={showError}
        disabled={loading}
      />
    </Modal>
  );
};

export default ManageRecipientModal;
